import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppConfirm } from "../../../utils/PopupUtil";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import API from "../../api/API";
import { newToastEvent, updateToastEvent } from "../../../utils/ToastUtil";
import { Person } from "../../../types/GlobalInterfaces";

interface Props {
  currentPerson: any;
  onSuccess: () => void;
}

const usePersonSupportal = (personID?: string | null) => {
  const queryClient = useQueryClient();

  const person = useQuery({
    queryKey: ["person", personID || ""],
    queryFn: () =>
      API.getPersonByID(personID ?? "").then(
        (data: any) => data.data.data.person
      ),
    enabled: personID ? true : false,
  });

  const credentials = useQuery({
    queryKey: ["credentials", personID],
    queryFn: () =>
      API.getPersonCredentials(personID ?? "").then(
        (data: any) => data.data.data.credentials
      ),
    enabled: personID ? true : false,
  });

  const deletePerson = useMutation({
    mutationFn: ({ currentPerson }: Props) => {
      newToastEvent("success", "Deleting user...", currentPerson.id, {
        loading: true,
        autoClose: false,
      });

      return API.deletePerson(currentPerson.id).then((data: any) => data);
    },
    onSuccess: (_, { currentPerson, onSuccess }: Props) => {
      let matchedQueryKeys: any[] = [];

      queryClient.setQueriesData<{ records: Person[] }>(
        {
          predicate: ({ queryKey }) => {
            const matched = queryKey.some((key) => key === "People");
            if (matched) matchedQueryKeys.push(queryKey);

            return matched;
          },
        },
        (prev) => {
          if (!Array.isArray(prev?.records)) return prev;

          return {
            ...prev,
            records: prev.records.filter(
              (user) => user?._id !== currentPerson?.id
            ),
          };
        }
      );

      for (const key of matchedQueryKeys) {
        queryClient.invalidateQueries(key);
      }

      callFunctionIfExists(onSuccess);
      updateToastEvent(
        "success",
        `Successfully deleted ${
          currentPerson.Name || currentPerson.PrimaryContactEmail || "User"
        }`,
        currentPerson.id,
        { autoClose: true, loading: false }
      );
    },
  });

  const deletePersonForReal = async (props: Props) => {
    const currentPerson = props.currentPerson;

    return AppConfirm({
      title: `Delete ${
        currentPerson.Name
          ? currentPerson.Name
          : currentPerson.PrimaryContactEmail
      }?`,
      text: `Are you sure you want to delete this person's account? This cannot be undone.`,
    }).then((result) => {
      if (!result.isConfirmed) return null;

      deletePerson.mutate(props);
    });
  };

  return {
    deletePerson: deletePersonForReal,
    person: person?.data,
    personError: person.error ? getErrorMessage(person.error) : null,
    personLoading: person?.isLoading,
    personCredentials: credentials.data,
    credentialsError: credentials.error
      ? getErrorMessage(credentials.error)
      : null,
    credentialsLoading: credentials.isLoading,
  };
};

export { usePersonSupportal };
